/**
 * The page a B2C sign-in was started from, so the user lands back on it (WI 3861).
 *
 * MSAL runs with `navigateToLoginRequestUrl: false` and B2C always answers on the redirect URI
 * (the site root), so without this every sign-in — a bookmark opened while signed out, a reload
 * after the session expired — ended on the role's home (`/extranet/admin` for an admin) instead
 * of the page that was asked for.
 *
 * sessionStorage, like the other per-tab keys of the app (`ext.b2cRedirectError`,
 * `ext.activeRole`, `ext.viewAsGroup`): the B2C round trip happens in the same tab, so the value
 * survives it, and another tab never inherits it. Written right before a sign-in redirect starts
 * and read once, by `initializeMsal`, when that redirect comes back with an account.
 */
const KEY = 'ext.returnUrl';

/** Pages a sign-in must never return to: the sign-in screen itself and the "no access" page. */
const NOT_A_DESTINATION = ['/login', '/unauthorized'];

/**
 * `url` as an in-app path to go back to, or null. Only same-origin paths are accepted (never an
 * absolute or protocol-relative URL — the value travels through storage, so it is treated as
 * untrusted), and the site root is null too: the router's own default route handles it.
 */
export function sanitizeReturnUrl(url: unknown, origin: string = globalThis.location?.origin ?? ''): string | null {
  if (typeof url !== 'string' || !url.startsWith('/') || url.length > 2048) return null;
  if (url.startsWith('//') || url.startsWith('/\\')) return null;
  let parsed: URL;
  try {
    parsed = new URL(url, origin || 'http://localhost');
  } catch {
    return null;
  }
  if (origin && parsed.origin !== origin) return null;
  const path = parsed.pathname;
  if (path === '/' || NOT_A_DESTINATION.some(p => path === p || path.startsWith(`${p}/`))) return null;
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/** Remember where to come back to after the sign-in that is about to start. */
export function rememberReturnUrl(url: string): void {
  const safe = sanitizeReturnUrl(url);
  try {
    if (safe) sessionStorage.setItem(KEY, safe);
    else sessionStorage.removeItem(KEY);
  } catch {
    // sessionStorage unavailable (private mode): the sign-in still works, it just lands on the home.
  }
}

/** The remembered page, at most once: reading it forgets it. */
export function takeReturnUrl(): string | null {
  try {
    const value = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return sanitizeReturnUrl(value);
  } catch {
    return null;
  }
}

/**
 * Put the remembered page in the address bar before the router's first navigation reads it, so a
 * sign-in that came back on the site root resumes on the requested page in a single load (no
 * detour through the role's home). Returns the restored path, or null when there was none.
 */
export function restoreReturnUrl(history: Pick<History, 'replaceState' | 'state'> = globalThis.history): string | null {
  const url = takeReturnUrl();
  if (url) history.replaceState(history.state, '', url);
  return url;
}
